// ---------------------------------------------------------------------------
// Shared CSRF token management
//
// Shared by fetchPatch.js, xhrPatch.js, and requestBatcher.js so they all
// use the same cached token and avoid duplicate /session/token calls.
//
// _nativeFetch is captured at module evaluation time — before any install*()
// function wraps window.fetch — so internal requests (/session/token,
// /subrequests) always bypass every wrapper and reach the real network.
// ---------------------------------------------------------------------------

import { DRUPAL_BASE_URL } from '../config/drupal.js';

const _nativeFetch = window.fetch.bind(window);

let _csrfToken = null;
let _csrfPromise = null;

/**
 * Returns a Promise resolving to the Drupal session CSRF token.
 * The token is fetched lazily on first call and cached for the session.
 * Subsequent calls return the cached value immediately.
 */
export function ensureCsrfToken() {
  if (_csrfToken) return Promise.resolve(_csrfToken);
  if (!_csrfPromise) {
    _csrfPromise = _nativeFetch('/session/token', { credentials: 'include' })
      .then(r => (r.ok ? r.text() : null))
      .then(t => { _csrfToken = t; return t; })
      .catch(() => null)
      .finally(() => { _csrfPromise = null; });
  }
  return _csrfPromise;
}

/**
 * Clears the cached CSRF token, forcing a fresh fetch on the next call to
 * ensureCsrfToken(). Call this after a 403 response indicates a stale token.
 */
export function resetCsrfToken() {
  _csrfToken = null;
}

/**
 * The true browser fetch, captured before any monkey-patch is applied.
 * Use this for internal requests (session/token, subrequests) that must
 * bypass all wrappers.
 */
export { _nativeFetch };

// ---------------------------------------------------------------------------
// Shared HTTP constants — used by both xhrPatch.js and fetchPatch.js
// ---------------------------------------------------------------------------

/** Pathname prefix pattern for Drupal API routes that require credentials. */
export const DRUPAL_API_PATTERN = /^\/(api|jsonapi|oauth|decoupled-api|session)\//;

/** HTTP methods that require a CSRF token in Drupal. */
export const MUTATING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * Drupal returns absolute URLs in submission_url after entity creation
 * (e.g. "https://host/decoupled-api/save?…&entity_id=105"), but our
 * DRUPAL_API_PATTERN regex expects a pathname starting with "/".
 * 
 * This normalises any URL (absolute or relative) to its pathname for reliable
 * pattern matching.
 *
 * @param {string|null|undefined} url
 * @returns {string|null}
 */
export function getPathname(url) {
  if (!url) return null;
  try { return new URL(url, location.origin).pathname; } catch (_) { return url; }
}

/**
 * Returns true when the URL resolves to the same origin as the current page.
 * Relative URLs (no scheme/host) are always same-origin.
 * Cross-origin requests (e.g. http://localhost:8000 from front-dev.orbit.ddev.site)
 * must NOT receive Drupal credentials or CSRF tokens.
 */
export function isSameOrigin(url) {
  if (!url) return true;
  try { return new URL(url, location.origin).origin === location.origin; } catch (_) { return true; }
}

let _drupalApiOrigin = null;
function getDrupalApiOrigin() {
  if (_drupalApiOrigin === null) {
    try { _drupalApiOrigin = new URL(DRUPAL_BASE_URL, location.origin).origin; }
    catch { _drupalApiOrigin = ''; }
  }
  return _drupalApiOrigin;
}

/**
 * Returns true when the URL targets a Drupal API endpoint that should receive
 * credentials and a CSRF token — either same-origin (proxied by nginx) or
 * cross-origin to DRUPAL_BASE_URL (e.g. absolute submission_url returned by
 * Drupal in the entity envelope, which points to the drupal.* subdomain even
 * when the front is served from front-dev.* or the root host).
 *
 * Drupal CORS (cors.config in services.yml) must allow the front origin with
 * supportsCredentials: true for the cross-origin case to actually work.
 */
export function isDrupalApiUrl(url) {
  if (!url) return false;
  try {
    const u = new URL(url, location.origin);
    if (!DRUPAL_API_PATTERN.test(u.pathname)) return false;
    return u.origin === location.origin || u.origin === getDrupalApiOrigin();
  } catch {
    return false;
  }
}
